import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useCurrentUserId } from "@src/appV2/ExperimentalHomeHealth/lib/useCurrentUserId";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { getVisitQueryKey } from "../../Cases/api/useVisit";
import { type VisitOccurrence } from "../../Cases/types";
import {
  type VisitOccurrencesParams,
  visitOccurrencesParamsSchema,
  visitOccurrencesResponseSchema,
} from "../types";

const VISIT_OCCURRENCES_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/visit-occurrences`;

export function getVisitOccurrencesQueryKey(params: {
  workplaceId: string;
  params: VisitOccurrencesParams;
}) {
  const keyWithFilter: Array<
    | string
    | VisitOccurrencesParams["filter"]
    | VisitOccurrencesParams["sort"]
    | VisitOccurrencesParams["page"]
  > = ["home-health", params.workplaceId, "visit-occurrences", params.params.filter];

  if (isDefined(params.params.sort)) {
    keyWithFilter.push(params.params.sort);
  }

  if (isDefined(params.params.page)) {
    keyWithFilter.push(params.params.page);
  }

  return keyWithFilter;
}

export function useVisitOccurrences(
  params: VisitOccurrencesParams,
  options: UseQueryOptions<VisitOccurrence[], unknown, VisitOccurrence[]> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: getVisitOccurrencesQueryKey({ workplaceId, params }),
    queryFn: async () => {
      const response = await get({
        url: VISIT_OCCURRENCES_URL.replace(":workplaceId", workplaceId),
        requestSchema: visitOccurrencesParamsSchema,
        queryParams: params,
        responseSchema: visitOccurrencesResponseSchema,
      });

      return response.data.data;
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
    onSuccess: (visits) => {
      visits.forEach((visit) => {
        queryClient.setQueryData(getVisitQueryKey({ workplaceId, visitId: visit.id }), visit);
      });
    },
    ...options,
  });
}
